import React, { useEffect, useState } from 'react'
import {
  IconButton,
  CircularProgress,
  Box,
  Button,
  TextField,
  Typography,
  Divider,
  Tooltip,
  alpha,
  useTheme
} from '@mui/material'
import GetAppIcon from '@mui/icons-material/GetApp'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import CloseIcon from '@mui/icons-material/Close'
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import ZoomInIcon from '@mui/icons-material/ZoomIn'
import ZoomOutIcon from '@mui/icons-material/ZoomOut'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'
import LanguageSelector from '../document-viewer/components/LanguageSelector'
import { isMobile } from '../../utils/utils'
import { fontSize } from '../../utils/fonts'
import { backendUrl } from '../../config'
import { useDispatch, useSelector } from 'react-redux'
import {
  setIsTranslationInProgress,
  setLoadingDocument,
  setTranslationProgress
} from '../../redux/actions/documentAction'
import { backendClient } from '../../api/backend'
import {
  setSearchLoadingDocument
} from '../../redux/actions/searchAction'

export const HtmlOptionsBar = ({
  documentSourceUrl,
  documentDoc,
  selectedCitation,
  iframeRef,
  scrolledIndex,
  numPages,
  scaleText,
  nextPage,
  prevPage,
  handleZoomIn,
  handleZoomOut,
  goToPage,
  setZoomLevel,
  zoomInEnabled,
  zoomOutEnabled,
  setDocument,
  showTranslationMenu = true
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()
  const tokenPayload = useSelector(state => state.auth.user?.token)
  const [isDownloading, setIsDownloading] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const [inputValue, setInputValue] = useState(`${scrolledIndex + 1}`)
  const [forceRender, setForceRender] = useState(0)
  const userLanguagePref = useSelector(state => state.user.appPreferences?.app_language || 'fr')
  const countryPref = useSelector(state => state.user?.countryPreference)
  const countryPreference = Array.isArray(countryPref) ? (countryPref[0] || 'MA') : (countryPref || 'MA')

  // Theme colors
  const primaryColor = theme.palette.primary.main
  const bgColor = theme.palette.background.paper
  const borderColor = theme.palette.divider
  const hoverBgColor = theme.palette.action.hover

  // Force re-render after component mount to ensure UI is updated
  useEffect(() => {
    const timer = setTimeout(() => {
      setForceRender(prev => prev + 1);
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  const handleLanguageChange = async valueTargetLanguage => {
    console.log('[handleLanguageChange] Starting translation process', {
      targetLanguage: valueTargetLanguage,
      documentLanguage: selectedCitation?.metadata?.language,
      documentId: selectedCitation?.metadata?.document_id
    })

    dispatch(setIsTranslationInProgress(true))
    dispatch(setLoadingDocument(true))
    dispatch(setSearchLoadingDocument(true))
    dispatch(setTranslationProgress({ progress: 0 }))

    const documentLanguage = selectedCitation?.metadata?.language
    console.log('[handleLanguageChange] Initial state set', {
      valueTargetLanguage,
      documentLanguage,
      documentUrl: documentDoc?.url
    })

    try {
      const containerName = process.env.REACT_APP_AZURE_CONTAINER_NAME

      // Use the currently loaded document URL — it is a valid SAS URL with the full blob path.
      const toTranslateUrl = documentDoc?.url
      if (!toTranslateUrl) {
        throw new Error('Failed to get source document for translation')
      }

      // Derive the translation target path by parsing the source blob URL and replacing
      // the language segment (second-to-last segment before the filename).
      //
      // Law path:      {country}/law/{jurisdiction_level}/{source}/{language}/{id}.html
      // Case-law path: {country}/case-law/{source}/{language}/{id}.html
      //
      // Both end with …/{language}/{id}.html, so we replace the language in place.
      let targetPath
      try {
        const parsedUrl = new URL(toTranslateUrl)
        // pathname = /container/ma/law/national/sgg.gov.ma/arabe/ar/doc123.html
        const parts = parsedUrl.pathname.split('/').filter(Boolean)
        const blobSegments = parts.slice(1) // drop container name
        const pathSegments = blobSegments.slice(0, -1) // drop filename
        // Pipeline always uses lowercase language codes (ar, fr, en).
        // Use lowercase so the cache lookup finds the original ingested document.
        pathSegments[pathSegments.length - 1] = valueTargetLanguage.toLowerCase()
        targetPath = pathSegments.join('/')
      } catch {
        // Fallback if URL parsing fails
        const docType = selectedCitation?.metadata?.doc_type === 'Jurisprudence' ? 'case-law' : 'law'
        const country = (selectedCitation?.metadata?.country || countryPreference || 'MA').toLowerCase()
        targetPath = `${country}/${docType}/${valueTargetLanguage.toLowerCase()}`
      }

      // We're in the HTML viewer — cached translations are always .html
      const baseFileName = selectedCitation?.metadata?.document_id
      const blobName = `${targetPath}/${baseFileName}.html`

      console.log('[handleLanguageChange] Attempting to fetch existing translated document', {
        containerName,
        targetPath,
        blobName,
        baseFileName
      })

      try {
        const data = await backendClient.getDocumentSas(
          containerName,
          blobName
        )

        console.log('[handleLanguageChange] Document fetch result', {
          success: !!data?.blob_url_with_sas,
          hasBlobUrl: !!data?.blob_url_with_sas
        })

        // Only proceed if we have a valid blob URL
        if (data?.blob_url_with_sas) {
          // Verify the blob actually exists by making a HEAD request
          const headResponse = await fetch(data.blob_url_with_sas, { method: 'HEAD' });
          if (headResponse.ok) {
            console.log('[handleLanguageChange] Using existing translated document')
            await dispatch(setDocument({ ...documentDoc, url: data.blob_url_with_sas, language: valueTargetLanguage }))
            dispatch(setTranslationProgress({ progress: 100 }))
            dispatch(setIsTranslationInProgress(false))
            dispatch(setLoadingDocument(false))
            dispatch(setSearchLoadingDocument(false))
            enqueueSnackbar(t('document_loaded_success'), { variant: 'success' })
            return
          }
        }

        // If we get here, either the blob doesn't exist or the HEAD request failed
        console.log('[handleLanguageChange] Document not found in target language, proceeding with translation')
      } catch (error) {
        console.log('[handleLanguageChange] Error checking existing document, proceeding with translation', {
          error: error.message,
          errorStack: error.stack
        })
      }

      // If we get here, we need to translate the document
      console.log('[handleLanguageChange] Starting translation process')

      console.log('[handleLanguageChange] Initiating translation request', {
        targetLanguage: valueTargetLanguage,
        sourceUrl: toTranslateUrl,
        targetPath
      })

      // Send translation request with target path
      const response = await fetch(`${backendUrl}api/translate/html`, {
        method: 'POST',
        headers: {
          Authorization: `${tokenPayload.token_type.charAt(0).toUpperCase()}${tokenPayload.token_type.slice(1)} ${tokenPayload.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          file_url: toTranslateUrl,
          target_language: valueTargetLanguage,
          target_path: targetPath  // Use the same target path as where we checked for existing document
        })
      })

      console.log('[handleLanguageChange] Translation request response', {
        status: response.status,
        ok: response.ok,
        hasBody: !!response.body,
        statusText: response.statusText
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('[handleLanguageChange] Translation request failed:', {
          status: response.status,
          statusText: response.statusText,
          errorData
        });
        throw new Error(errorData.detail || `Translation request failed with status: ${response.status}`);
      }

      if (!response.body) {
        throw new Error('No response body from translation service');
      }

      // Process the streaming response
      const reader = response.body.getReader()
      const decoder = new TextDecoder("utf-8")
      let buffer = ''
      let lastProgress = 0

      console.log('[handleLanguageChange] Starting to process translation stream')

      while (true) {
        const { done, value } = await reader.read()
        if (done) {
          console.log('[handleLanguageChange] Translation stream completed')
          break
        }

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n\n')

        for (let i = 0; i < lines.length - 1; i++) {
          const message = lines[i]
          if (!message.trim() || !message.startsWith('data: ')) continue

          try {
            const data = JSON.parse(message.replace('data: ', ''))
            console.log('[handleLanguageChange] Processing translation message', {
              hasError: !!data.error,
              hasProgress: data.progress !== undefined,
              isCompleted: !!data.completed,
              progress: data.progress,
              message: data.message
            })

            if (data.error) {
              throw new Error(`Translation error: ${data.error}`)
            }

            if (data.completed && data.translated_url) {
              console.log('[handleLanguageChange] Translation completed successfully', {
                translatedUrl: data.translated_url
              })
              dispatch(setDocument({ ...documentDoc, url: data.translated_url, language: valueTargetLanguage }))
              dispatch(setTranslationProgress({ message: "Terminé!", progress: 100 }))
              dispatch(setIsTranslationInProgress(false))
              dispatch(setLoadingDocument(false))
              dispatch(setSearchLoadingDocument(false))
              enqueueSnackbar(t('translated_success'), { variant: 'success' })
              return
            }

            if (data.progress !== undefined && data.progress !== lastProgress) {
              lastProgress = data.progress
              dispatch(setTranslationProgress(data))
            }
          } catch (e) {
            // Re-throw actual translation errors so the outer catch resets loading state.
            // Only swallow JSON parse errors.
            if (e.message && e.message.startsWith('Translation error:')) {
              throw e
            }
            console.error('[handleLanguageChange] Error parsing translation message:', {
              error: e.message,
              message: message,
              stack: e.stack
            })
          }
        }

        buffer = lines[lines.length - 1]
      }

      // Stream ended without a completion event — reset loading states.
      dispatch(setIsTranslationInProgress(false))
      dispatch(setLoadingDocument(false))
      dispatch(setSearchLoadingDocument(false))
      dispatch(setTranslationProgress({ progress: 0 }))
      enqueueSnackbar(t('translation_error'), { variant: 'error' })
    } catch (error) {
      console.error('[handleLanguageChange] Translation process failed:', {
        error: error.message,
        stack: error.stack,
        targetLanguage: valueTargetLanguage,
        documentLanguage: documentLanguage
      })
      enqueueSnackbar(t('translation_error'), { variant: 'error' })
      dispatch(setIsTranslationInProgress(false))
      dispatch(setLoadingDocument(false))
      dispatch(setSearchLoadingDocument(false))
      dispatch(setTranslationProgress({ progress: 0 }))
    }
  }

  const handleExport = async () => {
    setIsDownloading(true)

    // Update filename to .pdf since we're converting HTML to PDF
    const filename = selectedCitation?.metadata?.title
      ? `${selectedCitation?.metadata?.title}.pdf`
      : 'document.pdf'

    try {
      // Get the HTML file URL
      const htmlUrl = documentDoc?.url
      if (!htmlUrl) {
        throw new Error('No document URL available')
      }

      // Convert HTML to PDF using the URL
      const blob = await backendClient.exportDocument(
        htmlUrl,
        'url', // Pass URL instead of content
        filename
      )

      // Download the blob
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      enqueueSnackbar(t('document_export_success'), {
        variant: 'success'
      })
    } catch (error) {
      console.error('Error exporting document:', error)
      if (error.message === 'unauthorized_access') {
        enqueueSnackbar(t('unauthorized_access'), { variant: 'error' })
      } else if (error.message === 'session_expired') {
        enqueueSnackbar(t('session_expired'), { variant: 'error' })
      } else {
        enqueueSnackbar(t('export_error'), { variant: 'error' })
      }
    } finally {
      setIsDownloading(false)
    }
  }

  const handleChange = e => {
    setInputValue(e.target.value)
  }

  const handleKeyDown = e => {
    if (e.key === 'Enter') {
      const value = parseInt(inputValue, 10)
      if (!isNaN(value) && value > 0 && value <= numPages) {
        goToPage(value - 1)
      }
    }
  }

  useEffect(() => {
    setInputValue(`${scrolledIndex + 1}`)
  }, [scrolledIndex])

  // Check if we're in mobile mode
  const isMobileView = isMobile();
  // Ensure numPages is actually a number
  const hasPages = typeof numPages === 'number' && numPages > 0;

  // Custom button style
  const controlButtonStyle = {
    width: '36px !important',
    minWidth: '36px !important',
    height: '36px',
    padding: 0,
    borderRadius: '6px',
    color: primaryColor,
    '&:hover': {
      backgroundColor: alpha(primaryColor, 0.08),
    },
    '&.Mui-disabled': {
      color: alpha(primaryColor, 0.4),
    }
  };

  return (
    <Box
      display='flex'
      height='48px'
      width='100%'
      alignItems='center'
      justifyContent='space-between'
      sx={{
        backgroundColor: bgColor,
        borderBottom: `1px solid ${borderColor}`,
        borderRadius: '8px 8px 0 0',
        position: 'relative',
        zIndex: 2,
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
      }}
    >
      {/* Language Selector - Only show when showTranslationMenu is true */}
      {showTranslationMenu && (
        <>
          <Box className="language-selector-container" sx={{ flexShrink: 0 }}>
            <LanguageSelector
              disabled={false}
              currentLang={documentDoc?.language || selectedCitation?.metadata?.language || selectedCitation?.metadata?.source_language}
              onLanguageChange={handleLanguageChange}
              style={{
                flexShrink: 0,
                marginRight: 2
              }}
            />
          </Box>

          {/* Divider */}
          <Divider orientation="vertical" flexItem sx={{ mx: 1.5, display: { xs: 'none', sm: 'block' } }} />
        </>
      )}

      {/* Page Navigation - Always show when we have pages */}
      {hasPages && (
        <Box
          display='flex'
          alignItems='center'
          justifyContent='center'
          sx={{
            flexShrink: 1,
            display: { xs: isMobileView ? 'none' : 'flex', sm: 'flex' },
            backgroundColor: alpha(primaryColor, 0.04),
            borderRadius: '8px',
            padding: '2px',
            height: '38px'
          }}
        >
          <Tooltip title={t('previous_page')} arrow>
            <span>
              <Button
                onClick={prevPage}
                disabled={scrolledIndex === 0}
                variant='text'
                size='small'
                sx={controlButtonStyle}
              >
                <ArrowDropUpIcon />
              </Button>
            </span>
          </Tooltip>

          <TextField
            value={inputValue}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            inputProps={{
              style: {
                textAlign: 'center',
                fontSize: fontSize.small,
                padding: '4px 0',
                width: '36px',
                fontWeight: 600
              }
            }}
            sx={{
              width: '44px',
              margin: '0 4px',
              alignItems: 'center',
              justifyContent: 'center',
              '& .MuiInputBase-root': {
                height: '30px',
                display: 'flex',
                alignItems: 'center',
                backgroundColor: theme.palette.background.paper,
                borderRadius: '6px',
                '&.Mui-focused': {
                  boxShadow: `0 0 0 2px ${alpha(primaryColor, 0.2)}`
                }
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: alpha(primaryColor, 0.2)
              }
            }}
          />

          <Typography
            variant='body2'
            sx={{
              fontSize: fontSize.small,
              fontWeight: 500,
              color: '#4a5568',
              marginRight: '4px'
            }}
          >
            / {numPages}
          </Typography>

          <Tooltip title={t('next_page')} arrow>
            <span>
              <Button
                onClick={nextPage}
                disabled={scrolledIndex === numPages - 1}
                variant='text'
                size='small'
                sx={controlButtonStyle}
              >
                <ArrowDropDownIcon />
              </Button>
            </span>
          </Tooltip>
        </Box>
      )}

      {/* Zoom Controls - Always show when zoomInEnabled is true */}
      {zoomInEnabled && (
        <Box
          display='flex'
          alignItems='center'
          justifyContent='center'
          sx={{
            flexShrink: 1,
            marginLeft: 2,
            display: { xs: isMobileView ? 'none' : 'flex', sm: 'flex' },
            backgroundColor: alpha(primaryColor, 0.04),
            borderRadius: '8px',
            padding: '2px',
            height: '38px'
          }}
        >
          <Tooltip title={t('zoom_out')} arrow>
            <span>
              <Button
                onClick={handleZoomOut}
                disabled={!zoomOutEnabled}
                variant='text'
                size='small'
                sx={controlButtonStyle}
              >
                <ZoomOutIcon />
              </Button>
            </span>
          </Tooltip>

          <Typography
            variant='body2'
            sx={{
              mx: 2,
              fontSize: '0.875rem',
              fontWeight: 600,
              color: '#4a5568',
              minWidth: '52px',
              textAlign: 'center'
            }}
          >
            {scaleText || '100%'}
          </Typography>

          <Tooltip title={t('zoom_in')} arrow>
            <span>
              <Button
                onClick={handleZoomIn}
                disabled={!zoomInEnabled}
                variant='text'
                size='small'
                sx={controlButtonStyle}
              >
                <ZoomInIcon />
              </Button>
            </span>
          </Tooltip>
        </Box>
      )}

      {/* Divider */}
      <Divider orientation="vertical" flexItem sx={{ mx: 1.5, display: { xs: 'none', sm: 'block' } }} />

      {/* Download & Close Buttons */}
      <Box
        display='flex'
        alignItems='center'
        justifyContent='flex-end'
        style={{ flexShrink: 0 }}
        gap={0.5}
      >
        {selectedCitation?.metadata?.source_url && (
          <Tooltip title={t('source', 'Source')} arrow>
            <IconButton
              component="a"
              href={selectedCitation.metadata.source_url}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: primaryColor,
                backgroundColor: alpha(primaryColor, 0.04),
                borderRadius: '8px',
                padding: '8px',
                '&:hover': {
                  backgroundColor: alpha(primaryColor, 0.08),
                }
              }}
            >
              <OpenInNewIcon />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title={t('download')} arrow>
          <span>
            <IconButton
              onClick={handleExport}
              disabled={isDownloading}
              sx={{
                color: primaryColor,
                backgroundColor: alpha(primaryColor, 0.04),
                borderRadius: '8px',
                padding: '8px',
                '&:hover': {
                  backgroundColor: alpha(primaryColor, 0.08),
                }
              }}
            >
              {isDownloading ? <CircularProgress size={24} /> : <GetAppIcon />}
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={t('close')} arrow>
          <IconButton
            onClick={() => {
              if (setDocument) dispatch(setDocument(null))
            }}
            sx={{
              color: theme.palette.text.secondary,
              borderRadius: '8px',
              padding: '8px',
              '&:hover': {
                backgroundColor: alpha(theme.palette.text.secondary, 0.08),
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  )
}

export default HtmlOptionsBar